/**
 * Dutch national public holidays on Amsterdam calendar dates (YYYY-MM-DD).
 * Used for Sendcloud customer-ETA override deadline (feestdagen).
 *
 * Deno (Edge): same rules are inlined in `supabase/functions/sendcloud-shipment/index.ts`
 * — change both places when updating holidays or deadline logic.
 *
 * Covered: Nieuwjaar, 2e Paasdag, Koningsdag (observed), Hemelvaart, 2e Pinksterdag,
 * Kerst, 2e Kerstdag.
 *
 * Not included: Goede Vrijdag (geen algemene erkenningsdag), Bevrijdingsdag 5 mei
 * (niet elk jaar algemeen vrij — voeg later toe indien nodig).
 */

/** Current calendar date YYYY-MM-DD in Europe/Amsterdam (no dayjs). */
export function todayYmdEuropeAmsterdam(): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Amsterdam",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

const pad2 = (n: number) => String(n).padStart(2, "0");

function formatYmd(y: number, month1to12: number, day: number): string {
  return `${y}-${pad2(month1to12)}-${pad2(day)}`;
}

/** 0 = Sunday .. 6 = Saturday (UTC calendar date; YMD is a civil date). */
function weekdaySun0(y: number, m: number, d: number): number {
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Western Easter Sunday (Gregorian), returns YYYY-MM-DD. */
export function easterSundayYmd(year: number): string {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  // month: 3 = March, 4 = April
  return formatYmd(year, month, day);
}

function addDaysToYmd(ymd: string, days: number): string {
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(5, 7));
  const d = Number(ymd.slice(8, 10));
  const t = Date.UTC(y, m - 1, d + days);
  const u = new Date(t);
  return formatYmd(u.getUTCFullYear(), u.getUTCMonth() + 1, u.getUTCDate());
}

function isWeekendYmd(ymd: string): boolean {
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(5, 7));
  const d = Number(ymd.slice(8, 10));
  const w = weekdaySun0(y, m, d);
  return w === 0 || w === 6;
}

function weekdaySun0FromYmd(ymd: string): number {
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(5, 7));
  const d = Number(ymd.slice(8, 10));
  return weekdaySun0(y, m, d);
}

/** First Monday on or after `ymd` (same day if already Monday). */
function mondayOnOrAfterYmd(ymd: string): string {
  let x = ymd;
  while (weekdaySun0FromYmd(x) !== 1) {
    x = addDaysToYmd(x, 1);
  }
  return x;
}

/** First Monday on or after calendar `todayYmd + 1 day`. */
function nextMondayStrictlyAfterYmd(todayYmd: string): string {
  return mondayOnOrAfterYmd(addDaysToYmd(todayYmd, 1));
}

function maxYmd(a: string, b: string): string {
  return a >= b ? a : b;
}

const holidaySetCache = new Map<number, ReadonlySet<string>>();

function kingsDayObservedYmd(year: number): string {
  const w = weekdaySun0(year, 4, 27);
  // Sunday 27 April → Saturday 26 April; Saturday stays 27 April.
  if (w === 0) return formatYmd(year, 4, 26);
  return formatYmd(year, 4, 27);
}

function buildNlHolidaySet(year: number): ReadonlySet<string> {
  // When adding dates here, also add labels in `buildNlHolidayNameMap`.
  const s = new Set<string>();
  s.add(formatYmd(year, 1, 1));
  s.add(formatYmd(year, 12, 25));
  s.add(formatYmd(year, 12, 26));

  const easter = easterSundayYmd(year);
  s.add(addDaysToYmd(easter, 1)); // 2e Paasdag
  s.add(addDaysToYmd(easter, 39)); // Hemelvaart
  s.add(addDaysToYmd(easter, 50)); // 2e Pinksterdag
  s.add(kingsDayObservedYmd(year));

  return s;
}

export function nlPublicHolidayYmdSet(year: number): ReadonlySet<string> {
  let set = holidaySetCache.get(year);
  if (!set) {
    set = buildNlHolidaySet(year);
    holidaySetCache.set(year, set);
  }
  return set;
}

export function isNlPublicHolidayYmd(ymd: string): boolean {
  const y = Number(ymd.slice(0, 4));
  return nlPublicHolidayYmdSet(y).has(ymd);
}

const holidayNameMapCache = new Map<number, ReadonlyMap<string, string>>();

function buildNlHolidayNameMap(year: number): ReadonlyMap<string, string> {
  const m = new Map<string, string>();
  m.set(formatYmd(year, 1, 1), "Nieuwjaar");
  m.set(formatYmd(year, 12, 25), "Eerste kerstdag");
  m.set(formatYmd(year, 12, 26), "Tweede kerstdag");
  const easter = easterSundayYmd(year);
  m.set(addDaysToYmd(easter, 1), "Tweede paasdag");
  m.set(addDaysToYmd(easter, 39), "Hemelvaartsdag");
  m.set(addDaysToYmd(easter, 50), "Tweede pinksterdag");
  m.set(kingsDayObservedYmd(year), "Koningsdag");
  return m;
}

/** Dutch label for `ymd` if it is a modeled NL public holiday, else `null`. */
export function getNlPublicHolidayNameForYmd(ymd: string): string | null {
  const y = Number(ymd.slice(0, 4));
  let map = holidayNameMapCache.get(y);
  if (!map) {
    map = buildNlHolidayNameMap(y);
    holidayNameMapCache.set(y, map);
  }
  return map.get(ymd) ?? null;
}

/**
 * Inclusive last calendar day for `bookings.eta` before Sendcloud label creation
 * is blocked (Amsterdam civil dates).
 *
 * Starts at calendar tomorrow. Skips weekends and NL public holidays to find the
 * first "carrier-style" day `d`, counts how many NL holidays were in the skipped
 * chain from tomorrow up to (but not including) `d`, then adds that many calendar
 * days to `d` (Hemelvaart-type weeks).
 *
 * Then:
 * - If the deadline falls on **Saturday or Sunday**, move it to the **following
 *   Monday** (Friday-printed labels, Monday delivery).
 * - If **today** is an NL public holiday:
 *   - **Monday** holiday (e.g. Tweede Pinksterdag, Tweede paasdag): extend at least through
 *     **two calendar days after today** (Wed after a Mon bank holiday), so Fri–Sun label
 *     runs align with **May 27**-style ETAs instead of jumping to the *next* Monday.
 *   - **Any other weekday**: extend at least through the **first Monday strictly after today**
 *     (e.g. Hemelvaartsdag Thursday → Monday ETA).
 */
export function getSendcloudCustomerEtaOverrideDeadlineYmd(
  todayYmd: string
): string {
  let d = addDaysToYmd(todayYmd, 1);
  let holidayDaysSkipped = 0;

  while (isWeekendYmd(d) || isNlPublicHolidayYmd(d)) {
    if (isNlPublicHolidayYmd(d)) {
      holidayDaysSkipped += 1;
    }
    d = addDaysToYmd(d, 1);
  }

  let deadline = addDaysToYmd(d, holidayDaysSkipped);

  if (isWeekendYmd(deadline)) {
    deadline = mondayOnOrAfterYmd(deadline);
  }

  if (isNlPublicHolidayYmd(todayYmd)) {
    const w = weekdaySun0FromYmd(todayYmd);
    if (w === 1) {
      deadline = maxYmd(deadline, addDaysToYmd(todayYmd, 2));
    } else {
      deadline = maxYmd(deadline, nextMondayStrictlyAfterYmd(todayYmd));
    }
  }

  return deadline;
}

/**
 * Latest **weekday** that is not an NL public holiday on which the shipment can be
 * handed to the carrier, if the customer override ETA (`bookings.eta`) is
 * `deliveryEtaYmd` (Amsterdam calendar).
 *
 * Starts at **calendar day before** the ETA, then walks **backward** over weekends
 * and NL holidays (e.g. **Monday** delivery → **Friday** handover).
 */
export function getLatestShipmentHandoverYmdBeforeEta(
  deliveryEtaYmd: string
): string {
  let d = addDaysToYmd(deliveryEtaYmd, -1);
  let guard = 0;
  while ((isWeekendYmd(d) || isNlPublicHolidayYmd(d)) && guard < 21) {
    d = addDaysToYmd(d, -1);
    guard += 1;
  }
  return d;
}
